import { Catalog_VariantPackGroupItemsExpandedFieldsFragment } from '@/types';
import { create } from 'zustand';
import { NULL_CATEGORY_VALUE } from '@/utils/category';

export type CatalogGroupItemsStatus = 'loading' | 'loaded' | 'error';

export interface CatalogGroupItemsEntry {
  entries: Catalog_VariantPackGroupItemsExpandedFieldsFragment[];
  status: CatalogGroupItemsStatus;
}

export type CatalogGroupItemsLoader = (
  groupKeys: string[],
) => Promise<Catalog_VariantPackGroupItemsExpandedFieldsFragment[]>;

interface CatalogGroupItemsState {
  // Registers loader and scope - a new scope clears everything
  configure: (config: {
    loader: CatalogGroupItemsLoader | null;
    scopeKey: string | null;
  }) => void;
  groups: Record<string, CatalogGroupItemsEntry>;
  // Queues groups whose items should be loaded
  requestGroupItems: (groupKeys: string[]) => void;
  scopeKey: string | null;
}

export const FLUSH_DELAY_MS = 50;
export const MAX_GROUPS_PER_REQUEST = 20;

export const useCatalogGroupItemsStore = create<CatalogGroupItemsState>()((
  set,
  get,
) => {
  // Nothing renders from these so they stay out of state
  let loader: CatalogGroupItemsLoader | null = null;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  const pendingGroupKeys = new Set<string>();
  const requestedGroupKeys = new Set<string>();

  const setEntries = (updates: Record<string, CatalogGroupItemsEntry>) => {
    set((state) => ({ groups: { ...state.groups, ...updates } }));
  };

  const loadGroups = async (groupKeys: string[]) => {
    const requestScope = get().scopeKey;
    if (!loader) {
      return;
    }

    try {
      const rows = await loader(groupKeys);
      if (get().scopeKey !== requestScope) {
        return;
      }

      const groupEntryMap: Record<string, CatalogGroupItemsEntry> = {};
      for (const key of groupKeys) {
        groupEntryMap[key] = { entries: [], status: 'loaded' };
      }
      for (const row of rows) {
        const key = row.variant_pack_group_id || NULL_CATEGORY_VALUE;
        if (!groupEntryMap[key]) {
          groupEntryMap[key] = { entries: [], status: 'loaded' };
        }

        groupEntryMap[key].entries.push(row);
      }

      setEntries(groupEntryMap);
    } catch {
      if (get().scopeKey !== requestScope) {
        return;
      }

      // Let the next request retry
      const errored: Record<string, CatalogGroupItemsEntry> = {};
      for (const key of groupKeys) {
        requestedGroupKeys.delete(key);
        errored[key] = { entries: [], status: 'error' };
      }

      setEntries(errored);
    }
  };

  const flush = () => {
    flushTimer = null;

    const groupKeys = Array.from(pendingGroupKeys);
    pendingGroupKeys.clear();

    for (let i = 0; i < groupKeys.length; i += MAX_GROUPS_PER_REQUEST) {
      loadGroups(groupKeys.slice(i, i + MAX_GROUPS_PER_REQUEST));
    }
  };

  return {
    configure: (config) => {
      loader = config.loader;
      if (get().scopeKey === config.scopeKey) {
        return;
      }

      // Rows already on screen won't ask again on their own, so carry what
      // was requested into the new scope
      const previousGroupKeys = Array.from(requestedGroupKeys);

      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      pendingGroupKeys.clear();
      requestedGroupKeys.clear();
      set({ groups: {}, scopeKey: config.scopeKey });

      get().requestGroupItems(previousGroupKeys);
    },
    groups: {},
    requestGroupItems: (groupKeys) => {
      if (!loader || !get().scopeKey) {
        return;
      }

      const loading: Record<string, CatalogGroupItemsEntry> = {};
      for (const key of groupKeys) {
        if (!requestedGroupKeys.has(key)) {
          pendingGroupKeys.add(key);
          requestedGroupKeys.add(key);
          loading[key] = { entries: [], status: 'loading' };
        }
      }

      if (!Object.keys(loading).length) {
        return;
      }

      setEntries(loading);
      if (!flushTimer) {
        flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
      }
    },
    scopeKey: null,
  };
});
